import type { ActiveRideSnapshot, Lap, RideSample } from './model';

interface LapStart {
  t: number;
  dist: number;
  el: number;
  label?: string;
}

/** 1 Hz sample recorder with laps (SPEC §10.1). */
export class Recorder {
  samples: RideSample[] = [];
  laps: Lap[] = [];
  private lapStart: LapStart;

  constructor(startT: number, resume?: Pick<ActiveRideSnapshot, 'samples' | 'laps'>) {
    if (resume) {
      this.samples = [...resume.samples];
      this.laps = [...resume.laps];
      const last = this.samples[this.samples.length - 1];
      this.lapStart = { t: Date.now(), dist: last?.dist ?? 0, el: last?.el ?? 0 };
    } else {
      this.lapStart = { t: startT, dist: 0, el: 0 };
    }
  }

  add(s: RideSample): void {
    this.samples.push(s);
  }

  get lapCount(): number {
    return this.laps.length + 1;
  }

  get currentLapStart(): Readonly<LapStart> {
    return this.lapStart;
  }

  lap(trigger: Lap['trigger'], now: number, dist: number, el: number, nextLabel?: string): Lap {
    const lap: Lap = {
      startT: this.lapStart.t,
      endT: now,
      startDist: this.lapStart.dist,
      endDist: dist,
      startEl: this.lapStart.el,
      endEl: el,
      trigger,
      ...(this.lapStart.label ? { label: this.lapStart.label } : {}),
    };
    this.laps.push(lap);
    this.lapStart = { t: now, dist, el, ...(nextLabel ? { label: nextLabel } : {}) };
    return lap;
  }

  /** Close the final lap at session end (skips an empty trailing lap). */
  close(now: number, dist: number, el: number): void {
    if (el - this.lapStart.el >= 1 || this.laps.length === 0) this.lap('session_end', now, dist, el);
  }

  setCurrentLapLabel(label: string): void {
    this.lapStart.label = label;
  }
}
